import { Suspense, useEffect, useState } from 'react';
import { GlobalSearch } from './GlobalSearch';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { ChevronUp, Eye, KeyRound, LogOut, Menu, Repeat, X } from 'lucide-react';
import { consoleHomePath, useLogout, useSession } from '@/auth/session';
import { getErrorMessage } from '@/services/api';
import type { SellerSummary } from '@seller-portal/types';
import { ADMIN_NAV, NAV_GROUPS } from './nav';
import { Mark } from './Mark';
import { PageSpinner } from '@/components/ui/PageSpinner';
import { ExportsTray } from '@/features/exports/ExportsTray';

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || 'M'
  );
}

/**
 * Spec S00 and the prototype shell, rendered once as a layout route: a 216px ink sidebar (mark,
 * sections, account block), a 52px toolbar, and the page area where the routed page renders.
 * Moving between pages swaps only the page area, so the sidebar keeps its state and never
 * flickers; a page still loading shows its spinner inside the page area. The seller comes from
 * RequireSeller's outlet context (none on the staff and account pages). The exports tray lives
 * here too, so an export keeps reporting while the person moves around.
 */
export function ShellLayout() {
  const seller = useOutletContext<SellerSummary | undefined>();
  const { data: me } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [account, setAccount] = useState(false);
  const canSwitch = (me?.sellers.length ?? 0) > 1;
  const role = me?.user.isAdmin ? 'Admin' : 'Owner';
  const homeTo = consoleHomePath(me, seller);

  // Moving to another page closes the drawer and the account menu.
  useEffect(() => {
    setDrawer(false);
    setAccount(false);
  }, [location.pathname]);

  const signOut = () => logout.mutate(undefined, { onSuccess: () => navigate('/login') });

  const groups = seller
    ? NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          href: `/${seller.id}${item.to ? `/${item.to}` : ''}`,
          end: item.to === '',
        })),
      })).filter((g) => g.items.length > 0)
    : [];
  // Admin pages live in the account menu, so the seller's sections stay the whole sidebar.
  const adminItems = me?.user.isAdmin ? ADMIN_NAV : [];
  const readOnly = seller?.access === 'view';

  const menuItem =
    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-navy3 hover:text-white';

  const sidebar = (
    <div className="bg-ink text-nav flex h-full min-h-0 flex-col">
      <Link
        to={homeTo}
        className="font-display flex shrink-0 items-center gap-2 px-4 pb-4 pt-[18px] text-lg font-bold leading-none text-white"
      >
        <Mark /> merx
      </Link>

      {/* The sections scroll on their own; the logo and the account block never move. */}
      {groups.length > 0 ? (
        <nav className="sidebar-scroll flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain px-2.5 pb-2">
          {groups.map((group) => (
            <div key={group.label ?? 'top'} className="flex flex-col gap-0.5">
              {group.label ? (
                <div className="text-nav-section px-2.5 pb-1.5 pt-3.5 font-mono text-[10px] uppercase leading-none tracking-[.08em]">
                  {group.label}
                </div>
              ) : null}
              {group.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-[7px] px-2.5 py-[7px] font-medium ${
                      isActive
                        ? 'bg-navy3 text-white shadow-[inset_2px_0_0_#22BCE8]'
                        : 'hover:bg-navy2 hover:text-white'
                    }`
                  }
                >
                  {item.icon ? <item.icon size={16} strokeWidth={1.8} aria-hidden="true" /> : null}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      ) : (
        <div className="flex-1" />
      )}

      <div className="border-navy2 relative shrink-0 border-t px-2.5 pb-3 pt-2.5">
        {account ? (
          <div
            id="account-menu"
            className="border-navy3 bg-navy2 absolute bottom-full left-2.5 right-2.5 mb-1 max-h-[70vh] overflow-y-auto rounded-lg border p-1.5 shadow-xl"
          >
            {adminItems.length > 0 ? (
              <>
                <div className="text-nav-section px-2 pb-1 pt-1 font-mono text-[10px] uppercase tracking-[.08em]">
                  Admin
                </div>
                {adminItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    // Onboarding sits under Seller support's path, so Seller support matches its own page only.
                    end
                    className={({ isActive }) =>
                      `${menuItem} ${isActive ? 'bg-navy3 text-white' : ''}`
                    }
                  >
                    {item.icon ? <item.icon size={13} aria-hidden="true" /> : null} {item.label}
                  </NavLink>
                ))}
                <div className="border-navy3 my-1.5 border-t" />
              </>
            ) : null}
            {canSwitch ? (
              <Link to="/select-seller" className={menuItem}>
                <Repeat size={13} aria-hidden="true" /> Switch seller
              </Link>
            ) : null}
            <Link to="/account/password" className={menuItem}>
              <KeyRound size={13} aria-hidden="true" /> Password
            </Link>
            <button
              type="button"
              onClick={signOut}
              disabled={logout.isPending}
              className={`${menuItem} disabled:opacity-50`}
            >
              <LogOut size={13} aria-hidden="true" />{' '}
              {logout.isPending ? 'Signing out…' : 'Sign out'}
            </button>
            {logout.isError ? (
              // The cookie is httpOnly: a failed sign-out leaves the person signed in. Say so.
              <p role="alert" className="text-red px-2 pb-1 pt-1.5 text-[11px]">
                Sign out failed: {getErrorMessage(logout.error)}
              </p>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setAccount((open) => !open)}
          aria-expanded={account}
          aria-controls="account-menu"
          className="bg-navy2 hover:bg-navy3 flex w-full items-center gap-2 rounded-lg p-2 text-left"
        >
          <span className="from-cyan to-blue font-display grid h-7 w-7 shrink-0 place-items-center rounded-[7px] bg-gradient-to-br text-xs font-bold text-white">
            {initialsOf(seller?.name ?? me?.user.email ?? '')}
          </span>
          <span className="min-w-0 flex-1">
            <b className="block truncate text-[12.5px] font-semibold text-white">
              {seller?.name ?? 'No seller selected'}
            </b>
            <span className="text-nav-dim block truncate text-[11px]">
              {me?.user.email} · {role}
            </span>
          </span>
          <ChevronUp
            size={14}
            className={`text-nav-dim shrink-0 transition ${account ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-[216px] shrink-0 overflow-hidden md:block">
        {sidebar}
      </aside>

      {/* Small screens: the same sidebar as a drawer. */}
      {drawer ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawer(false)}
            className="bg-ink/40 absolute inset-0"
          />
          <div
            id="shell-menu"
            className="absolute inset-y-0 left-0 w-[264px] overflow-hidden shadow-2xl"
          >
            {sidebar}
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-line bg-panel flex h-[52px] shrink-0 items-center gap-3 border-b px-4 md:px-5">
          <button
            type="button"
            onClick={() => setDrawer(true)}
            aria-expanded={drawer}
            aria-controls="shell-menu"
            aria-label="Open menu"
            className="border-line text-ink hover:bg-hover grid h-8 w-8 place-items-center rounded-lg border md:hidden"
          >
            {drawer ? <X size={16} aria-hidden="true" /> : <Menu size={16} aria-hidden="true" />}
          </button>
          <Link
            to={homeTo}
            className="font-display flex items-center gap-2 text-base font-bold leading-none md:hidden"
          >
            <Mark size={22} /> merx
          </Link>
          {seller ? <GlobalSearch seller={seller} /> : null}
          <span className="flex-1" />
        </header>

        {readOnly && seller ? (
          <div
            role="status"
            className="border-line bg-amber-bg flex items-center gap-2 border-b px-4 py-1.5 text-xs text-[#7A4A06] md:px-6"
          >
            <Eye size={14} aria-hidden="true" />
            <span>
              <b className="font-semibold">Support view · read-only</b> — you can look around{' '}
              {seller.name}, but changes are turned off.
            </span>
          </div>
        ) : null}

        <main className="flex flex-1 flex-col gap-4 px-4 py-5 md:px-6 md:pb-10">
          <Suspense fallback={<PageSpinner inline />}>
            <Outlet context={seller} />
          </Suspense>
        </main>
      </div>
      {/* Exports need edit access (each takes a seller slot), so support view has no tray. */}
      {seller && seller.access === 'edit' ? <ExportsTray sellerId={seller.id} /> : null}
    </div>
  );
}
